<script setup lang="ts">
import { computed } from 'vue'
import PrimeSelect from 'primevue/select'
import type { SelectOption } from '@shared/types/form'

export type SelectSize = 'small' | 'medium'
/**
 * `default` — full-width field, for forms.
 * `chip` — compact inline pill that hugs its label, for in-card filters
 *          (period / fiscal-year pickers in a card header).
 */
export type SelectVariant = 'default' | 'chip'

interface Props {
  modelValue: string
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  size?: SelectSize
  variant?: SelectVariant
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  invalid: false,
  size: 'medium',
  variant: 'default',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const classes = computed(() => [
  'base-select',
  `base-select--${props.size}`,
  `base-select--${props.variant}`,
])
</script>

<template>
  <PrimeSelect
    :modelValue="props.modelValue"
    :options="props.options"
    option-label="label"
    option-value="value"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :invalid="props.invalid"
    :class="classes"
    :pt="{
      label: { class: 'base-select__label' },
    }"
    @update:modelValue="emit('update:modelValue', $event)"
  />
</template>

<style scoped>
:deep(.base-select.p-select) {
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--p-surface-300, #d5d7da);
  background: var(--p-surface-0, #fafafa);
}

:deep(.base-select--medium.p-select) {
  height: 44px;
}

:deep(.base-select--small.p-select) {
  height: 36px;
}

:deep(.base-select__label) {
  color: var(--p-text-muted-color, #0055ff) !important;
  padding: 0.7rem 1rem !important;
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
  font-weight: 400 !important;
}

:deep(.base-select--medium .base-select__label) {
  font-size: 0.9375rem !important;
}

:deep(.base-select--small .base-select__label) {
  font-size: 0.8125rem !important;
}

/* ── Variant: chip ── declared after the sizes so it wins on equal specificity ── */
:deep(.base-select--chip.p-select) {
  width: auto;
  height: auto;
  min-height: 1.75rem;
  border-radius: 8px;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
}

:deep(.base-select--chip .base-select__label) {
  padding: 0.25rem 0.125rem 0.25rem 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: var(--p-text-muted-color) !important;
  white-space: nowrap;
}

:deep(.base-select--chip .p-select-dropdown) {
  width: auto;
  padding-right: 0.75rem;
  color: var(--p-text-muted-color);
}

:deep(.base-select__label.p-placeholder) {
  color: var(--p-text-muted-color, #717680) !important;
  font-weight: 400 !important;
}

:deep(.base-select.p-select:not(.p-disabled):hover) {
  border-color: var(--p-primary-300, #a5a0ff);
}

:deep(.base-select.p-select.p-focus) {
  border-color: var(--p-primary-400, #7e79ff);
  box-shadow: none;
  outline: none;
}
</style>
